import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useFilters } from '@panwds/react-filters';
import { Tooltip } from '@panwds/react-ui';
import dayjs from '../../../shared/utils/date';
import styles from './ProductUptimeBar.module.scss';
import {
    normalizeProductType,
    FILTER_CONTEXT_ID,
    isComponentAffected,
} from './statusTableUtils.jsx';
import { parseRegionFilterSelections, normalizeCountry } from '../../../shared/utils/regions';
import { STATUS_TYPE_ALL_SELECTED } from '../map/MapStatusBar';
import { formatCustomRange } from '../../../shared/utils/filters';
import UtcNotice from '../../../shared/components/UtcNotice';

const TIME_RANGE_CONFIG = {
    '7d':  { days: 7,   granularity: 'day',  label: '7 days ago' },
    '30d': { days: 30,  granularity: 'day',  label: '30 days ago' },
    '90d': { days: 90,  granularity: 'day',  label: '90 days ago' },
    '1y':  { days: 365, granularity: 'week', label: '1 year ago' },
};

const DEFAULT_TIME_RANGE = '90d';

const ChevronRight = ({ className }) => (
    <svg className={className} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M6 3L11 8L6 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

// ─── Uptime calculation helpers ──────────────────────────────────────────────

/**
 * Returns all region cities affected by an incident, derived from its plane data.
 */
const getIncidentCities = (incident) => {
    const planes = incident?.Planes || incident?.planes;
    if (!planes) return [];
    const cities = new Set();
    planes?.control?.regions?.forEach(r => { if (r.city) cities.add(r.city); });
    planes?.data?.regions?.forEach(r => { if (r.city) cities.add(r.city); });
    return [...cities];
};

/**
 * Returns the effective end timestamp for a disruption incident.
 * PRD: duration = Investigating (created_at) → Monitoring (restored).
 * Falls back to resolved_at, then now (ongoing).
 */
const getDisruptionEnd = (incident) => {
    const updates = incident?.updates || [];
    for (const update of updates) {
        if (update?.status?.toLowerCase() === 'monitoring') {
            const ts = dayjs(update.timestamp);
            if (ts.isValid()) return ts;
        }
    }
    if (incident.resolved_at) {
        const ts = dayjs(incident.resolved_at);
        if (ts.isValid()) return ts;
    }
    return dayjs(); // ongoing
};

/**
 * Returns the full lifecycle end of an incident for overlap / tooltip matching.
 * Uses resolved_at if present; falls back to now (ongoing).
 * Distinct from getDisruptionEnd which returns the service-restore point used for uptime math.
 */
const getIncidentEnd = (incident) => {
    if (incident.resolved_at) {
        const ts = dayjs(incident.resolved_at);
        if (ts.isValid()) return ts;
    }
    return dayjs(); // ongoing
};

/**
 * Merges overlapping [startMs, endMs] intervals and returns total duration in minutes.
 */
const unionMinutes = (rawIntervals) => {
    const valid = rawIntervals.filter(i => i.endMs > i.startMs);
    if (!valid.length) return 0;
    const sorted = [...valid].sort((a, b) => a.startMs - b.startMs);
    const merged = [{ ...sorted[0] }];
    for (let i = 1; i < sorted.length; i++) {
        const last = merged[merged.length - 1];
        if (sorted[i].startMs <= last.endMs) {
            last.endMs = Math.max(last.endMs, sorted[i].endMs);
        } else {
            merged.push({ ...sorted[i] });
        }
    }
    return merged.reduce((sum, iv) => sum + (iv.endMs - iv.startMs), 0) / 60_000;
};

/**
 * Clips disruption incidents to a [periodStartMs, periodEndMs] window and returns
 * the unioned total disruption minutes within that window for one set of incidents.
 */
const disruptionMinutesInWindow = (disruptions, periodStartMs, periodEndMs) => {
    const intervals = disruptions.map(inc => ({
        startMs: Math.max(dayjs(inc.created_at).valueOf(), periodStartMs),
        endMs:   Math.min(getDisruptionEnd(inc).valueOf(),   periodEndMs),
    })).filter(i => i.startMs < periodEndMs && i.endMs > periodStartMs);
    return unionMinutes(intervals);
};

/**
 * PRD AC3 formula:
 *   Per-region uptime = (totalMinutes − disruptionMinutes) / totalMinutes
 *   Service uptime    = average of per-region uptimes
 *
 * When inScopeRegionCities is null/empty (all-regions default), computes globally.
 */
const computeUptimePercentDuration = (disruptions, periodStartMs, periodEndMs, inScopeRegionCities) => {
    const totalMinutes = (periodEndMs - periodStartMs) / 60_000;
    if (totalMinutes <= 0) return '100.00';
    if (!disruptions.length) return '100.00';

    const format = (pct, hasDowntime) => {
        const s = pct.toFixed(2);
        if (hasDowntime && s === '100.00') return '99.99';
        return s;
    };

    if (!inScopeRegionCities?.length) {
        const dMin = disruptionMinutesInWindow(disruptions, periodStartMs, periodEndMs);
        const pct = Math.max(0, (totalMinutes - dMin) / totalMinutes) * 100;
        return format(pct, dMin > 0);
    }

    // Per-region averaging — PRD: each region counted equally
    let totalUptime = 0;
    let anyDowntime = false;
    for (const city of inScopeRegionCities) {
        const cityDisruptions = disruptions.filter(inc => {
            const cities = getIncidentCities(inc);
            return !cities.length || cities.includes(city);
        });
        const dMin = disruptionMinutesInWindow(cityDisruptions, periodStartMs, periodEndMs);
        if (dMin > 0) anyDowntime = true;
        totalUptime += Math.max(0, (totalMinutes - dMin) / totalMinutes * 100);
    }
    return format(totalUptime / inScopeRegionCities.length, anyDowntime);
};

/**
 * Determine the heatmap color for a bucket.
 * Color semantics:
 *   Green  – no incidents
 *   Amber  – Degraded only, or Disruption that doesn't affect all in-scope regions
 *   Red    – Disruption (with or without Degraded) AND all in-scope regions affected
 */
const computeSegmentColor = (matchedIncidents, inScopeRegionCities) => {
    if (!matchedIncidents.length) return null;

    const disruptions = matchedIncidents.filter(i => i.severity?.toLowerCase() === 'disruption');

    if (!disruptions.length) return 'degraded'; // only degraded → amber

    // Disruption exists — check whether ALL in-scope regions are affected
    if (!inScopeRegionCities?.length) return 'degraded'; // no region info → amber (conservative)

    const affectedCities = new Set(disruptions.flatMap(getIncidentCities));
    // Incidents with no city data are treated as global (affect all regions)
    const hasGlobalDisruption = disruptions.some(inc => !getIncidentCities(inc).length);

    if (hasGlobalDisruption || inScopeRegionCities.every(city => affectedCities.has(city))) {
        return 'disruption'; // red — all in-scope regions are down
    }
    return 'degraded'; // amber — some in-scope regions remain healthy
};

/**
 * Compute per-bucket status segments for the selected time range window.
 * Uses getDisruptionEnd() so the incident's active window matches the duration calculation.
 */
const computeUptimeSegments = (filteredIncidents, rangeConfig, inScopeRegionCities) => {
    const { days, granularity } = rangeConfig;
    const bucketSize = granularity === 'week' ? 7 : 1;
    const numBuckets = Math.ceil(days / bucketSize);
    const segments = [];

    // For custom ranges the window may not end at "now"; anchor buckets from customEnd.
    // Day buckets are UTC days, matching the calendars and the incident timestamps.
    const windowEnd = rangeConfig.customEnd || dayjs.utc();

    for (let j = numBuckets - 1; j >= 0; j--) {
        const bucketEnd   = windowEnd.subtract(j * bucketSize, 'day').endOf('day');
        const bucketStart = bucketEnd.subtract(bucketSize - 1, 'day').startOf('day');

        const matchedIncidents = [];

        for (const incident of filteredIncidents) {
            if (!incident?.severity) continue;
            const incStart = dayjs(incident.created_at);
            if (!incStart.isValid()) continue;
            // Use full lifecycle window (resolved_at or now) for tooltip matching.
            // getDisruptionEnd (monitoring-restore time) is reserved for uptime % math.
            const incEnd = getIncidentEnd(incident);
            const overlaps = !incStart.isAfter(bucketEnd) && !incEnd.isBefore(bucketStart);
            if (!overlaps) continue;

            const incId = incident.custom_incident_id || incident.incident_id || incident.id;
            if (!matchedIncidents.find(i => (i.custom_incident_id || i.incident_id || i.id) === incId)) {
                matchedIncidents.push(incident);
            }
        }

        segments.push({
            date:      bucketEnd.format('YYYY-MM-DD'),
            startDate: bucketStart.format('YYYY-MM-DD'),
            worstSeverity: computeSegmentColor(matchedIncidents, inScopeRegionCities),
            matchedIncidents,
        });
    }
    return segments;
};

// ─── Sub-components ──────────────────────────────────────────────────────────

const IncidentPopup = ({ seg, anchorRect, product, serviceName, onMouseEnter, onMouseLeave, forwardedParams }) => {
    const navigate = useNavigate();
    if (!anchorRect || !seg?.matchedIncidents?.length) return null;

    const startDate = seg.startDate || seg.date;
    const endDate   = seg.date;
    const isSingleDay = startDate === endDate;
    const dateLabel = `${isSingleDay
        ? dayjs(seg.date).format('MMM D, YYYY')
        : `${dayjs(startDate).format('MMM D')} – ${dayjs(endDate).format('MMM D, YYYY')}`} UTC`;

    const incidentsUrl = `/incidents?products=${encodeURIComponent(product || '')}&range=${encodeURIComponent(formatCustomRange(startDate, endDate))}${forwardedParams || ''}`;

    const headerTooltip = serviceName
        ? `View all incidents for ${serviceName} during this period`
        : `View all incidents during this period`;

    const popupStyle = {
        position: 'fixed',
        zIndex: 9999,
        left: Math.max(8, Math.min(anchorRect.left + anchorRect.width / 2 - 130, window.innerWidth - 280)),
        top: anchorRect.top - 8,
        transform: 'translateY(-100%)',
        minWidth: 220,
        maxWidth: 320,
    };

    return createPortal(
        <div
            className={styles.incPopup}
            style={popupStyle}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            onClick={(e) => e.stopPropagation()}
        >
            <button
                className={`${styles.incPopupHeader} ${styles.incPopupHeaderLink}`}
                data-tooltip={headerTooltip}
                onClick={(e) => { e.stopPropagation(); navigate(incidentsUrl); }}
            >
                {dateLabel} &#8594;
            </button>
            {seg.matchedIncidents.map((inc, idx) => {
                const sev    = inc.severity?.toLowerCase();
                const incId  = inc.custom_incident_id || inc.incident_id || inc.id;
                const incTitle = inc.title || null;
                return (
                    <div key={idx} className={styles.incPopupRow}>
                        <span className={styles.incPopupBullet}>•</span>
                        <button
                            className={styles.incLink}
                            data-tooltip={incTitle || incId}
                            onClick={(e) => { e.stopPropagation(); navigate(`/incidents/${incId || ''}`); }}
                        >
                            {incId}
                        </button>
                        <span className={`${styles.sevtag} ${styles[`sevtag_${sev}`]}`}>
                            {sev === 'disruption' ? 'Disrupted' : sev === 'degraded' ? 'Degraded' : inc.severity}
                        </span>
                    </div>
                );
            })}
        </div>,
        document.body,
    );
};

const UptimeBarStrips = ({ segments, onSegmentClick, product, serviceName, forwardedParams }) => {
    const [activePopup, setActivePopup] = useState(null);
    const hideTimerRef = useRef(null);

    const showPopup = (seg, rect) => {
        clearTimeout(hideTimerRef.current);
        if (seg.matchedIncidents.length > 0) setActivePopup({ seg, rect });
    };
    const hidePopup = () => {
        hideTimerRef.current = setTimeout(() => setActivePopup(null), 200);
    };

    return (
        <div className={styles.barContainer}>
            {segments.map((seg, i) => {
                const severity = seg.worstSeverity;
                let segClass = styles.segOperational;
                if (severity === 'disruption') segClass = styles.segDisruption;
                else if (severity === 'degraded')  segClass = styles.segDegraded;

                const hasIncident = seg.matchedIncidents.length > 0;
                const dateLabel = `${seg.startDate && seg.startDate !== seg.date
                    ? `${dayjs(seg.startDate).format('MMM D')} – ${dayjs(seg.date).format('MMM D, YYYY')}`
                    : dayjs(seg.date).format('MMM D, YYYY')} UTC`;

                const bar = (
                    <div
                        key={i}
                        className={`${styles.segment} ${segClass} ${hasIncident ? styles.segHasIncident : ''}`}
                        onClick={hasIncident ? () => onSegmentClick(seg.startDate || seg.date, seg.date) : undefined}
                        onMouseEnter={hasIncident ? (e) => showPopup(seg, e.currentTarget.getBoundingClientRect()) : undefined}
                        onMouseLeave={hasIncident ? hidePopup : undefined}
                    />
                );

                if (!hasIncident) {
                    return (
                        <Tooltip key={i} label={dateLabel} defaultPlacement="top">
                            {bar}
                        </Tooltip>
                    );
                }
                return bar;
            })}
            {activePopup && (
                <IncidentPopup
                    seg={activePopup.seg}
                    anchorRect={activePopup.rect}
                    product={product}
                    serviceName={serviceName}
                    onMouseEnter={() => clearTimeout(hideTimerRef.current)}
                    onMouseLeave={() => setActivePopup(null)}
                    forwardedParams={forwardedParams}
                />
            )}
        </div>
    );
};

/** Service row: heatmap bar + uptime % label (uptime shown at service level only, per PRD). */
const BarCellContent = ({ segments, disruptions, periodStartMs, periodEndMs, inScopeRegionCities, onSegmentClick, product, serviceName, forwardedParams }) => {
    const uptimePct = useMemo(
        () => computeUptimePercentDuration(disruptions, periodStartMs, periodEndMs, inScopeRegionCities),
        [disruptions, periodStartMs, periodEndMs, inScopeRegionCities],
    );
    const pctClass = parseFloat(uptimePct) >= 100 ? styles.pctGood : styles.pctNeutral;
    return (
        <div className={styles.barWithPct}>
            <UptimeBarStrips segments={segments} onSegmentClick={onSegmentClick} product={product} serviceName={serviceName} forwardedParams={forwardedParams} />
            <span className={`${styles.pctLabel} ${pctClass}`}>
                {uptimePct}%
            </span>
        </div>
    );
};

/** Product / Plane grouping header rows: heatmap bar only — no uptime % (per PRD AC2/AC3). */
const StatusBarOnly = ({ segments, onSegmentClick, product, serviceName, forwardedParams }) => (
    <div className={styles.barWithPct}>
        <UptimeBarStrips segments={segments} onSegmentClick={onSegmentClick} product={product} serviceName={serviceName} forwardedParams={forwardedParams} />
        <span className={styles.pctSpacer} />
    </div>
);

// ─── Skeleton bar ─────────────────────────────────────────────────────────────

const SkeletonBar = ({ segmentCount }) => (
    <div className={styles.barWithPct}>
        <div className={styles.barContainer}>
            {Array.from({ length: segmentCount }).map((_, i) => (
                <div key={i} className={styles.segSkeleton} />
            ))}
        </div>
        <span className={styles.pctSkeleton} />
    </div>
);

// ─── Skeleton product row (products loading phase) ────────────────────────────

const SkeletonProductRow = ({ segmentCount, nameWidth = '40%' }) => (
    <tr className={styles.productRow}>
        <td className={styles.productNameCell}>
            <span className={styles.nameSkeleton} style={{ width: nameWidth }} />
        </td>
        <td className={styles.barCell}>
            <SkeletonBar segmentCount={segmentCount} />
        </td>
    </tr>
);

// ─── Main component ──────────────────────────────────────────────────────────

const ProductUptimeBar = ({ incidents, isLoading, isLoadingProducts, products, allRegions, timeRange, customHistoricRange, onHelpClick }) => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { filters: pageFilters } = useFilters(FILTER_CONTEXT_ID);
    const [isOpen, setIsOpen]     = useState(true);
    const [openProducts, setOpenProducts] = useState(new Set());
    const [openPlanes, setOpenPlanes]     = useState({});

    const legendRef = useRef(null);
    const [legendBottom, setLegendBottom] = useState(171);

    useEffect(() => {
        const el = legendRef.current;
        if (!el) return;
        const update = () => {
            const filterBarBase = document.documentElement.getAttribute('data-filter-scope') === 'active'
                ? 191
                : 171;
            setLegendBottom(filterBarBase + el.getBoundingClientRect().height);
        };
        update();
        const ro = new ResizeObserver(update);
        ro.observe(el);
        // Re-run when the scope summary attribute changes on <html>
        const mo = new MutationObserver(update);
        mo.observe(document.documentElement, { attributes: true, attributeFilter: ['data-filter-scope'] });
        return () => { ro.disconnect(); mo.disconnect(); };
    }, []);

    const theadRef = useRef(null);
    const [theadHeight, setTheadHeight] = useState(44);

    useEffect(() => {
        const el = theadRef.current;
        if (!el) return;
        const update = () => setTheadHeight(el.getBoundingClientRect().height);
        update();
        const ro = new ResizeObserver(update);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    const productStickyTop = legendBottom + theadHeight;

    const rangeConfig = useMemo(() => {
        if (timeRange === 'custom' && customHistoricRange?.start && customHistoricRange?.end) {
            const startD = dayjs.utc(customHistoricRange.start).startOf('day');
            const endD   = dayjs.utc(customHistoricRange.end).endOf('day');
            const days   = Math.max(1, endD.diff(startD, 'day') + 1);
            return {
                days,
                granularity: days > 90 ? 'week' : 'day',
                label: `${dayjs(customHistoricRange.start).format('MMM D')} – ${dayjs(customHistoricRange.end).format('MMM D')}`,
                customStart: startD,
                customEnd: endD,
            };
        }
        return TIME_RANGE_CONFIG[timeRange] || TIME_RANGE_CONFIG[DEFAULT_TIME_RANGE];
    }, [timeRange, customHistoricRange]);

    // Period boundaries used for duration-based uptime calculation
    const { periodStartMs, periodEndMs } = useMemo(() => {
        if (rangeConfig.customStart && rangeConfig.customEnd) {
            return {
                periodStartMs: rangeConfig.customStart.valueOf(),
                periodEndMs: rangeConfig.customEnd.valueOf(),
            };
        }
        const endMs   = dayjs.utc().endOf('day').valueOf();
        const startMs = dayjs.utc().subtract(rangeConfig.days, 'day').startOf('day').valueOf();
        return { periodStartMs: startMs, periodEndMs: endMs };
    }, [rangeConfig]);

    // Resolve which cities are currently in scope for region-aware uptime and color logic
    const inScopeRegionCities = useMemo(() => {
        const selectedRegions = pageFilters?.region || [];
        if (!selectedRegions.length || !allRegions?.length) return null;

        const parsed = parseRegionFilterSelections(selectedRegions);

        if (parsed.isAllSelected) {
            // All continents selected — use all known cities
            return allRegions.map(r => r.city).filter(Boolean);
        }

        const cities = new Set(parsed.selectedCities);
        allRegions.forEach(r => {
            if (!r.city) return;
            if (parsed.selectedContinents.includes(r.continent)) {
                cities.add(r.city);
            } else if (parsed.selectedCountries.includes(normalizeCountry(r.country))) {
                cities.add(r.city);
            }
        });
        return cities.size > 0 ? [...cities] : null;
    }, [pageFilters?.region, allRegions]);

    const regionNavParam = pageFilters?.region?.length
        ? `&region=${encodeURIComponent(pageFilters.region.join(','))}`
        : '';
    const severityNavParam = pageFilters?.statusType && pageFilters.statusType !== STATUS_TYPE_ALL_SELECTED
        ? `&severity=${pageFilters.statusType}`
        : '';
    const rangeNavParam = useMemo(() => {
        if (timeRange === 'custom' && customHistoricRange?.start && customHistoricRange?.end) {
            return `&from=${customHistoricRange.start}&to=${customHistoricRange.end}`;
        }
        if (timeRange && timeRange !== 'custom') {
            return `&range=${timeRange}`;
        }
        return '';
    }, [timeRange, customHistoricRange]);

    // Forward current products/regions URL params to incident list navigations so the
    // incidents tab opens with the same filter context as the historic view.
    const forwardedSearchString = useMemo(() => {
        const p = new URLSearchParams();
        const currentProducts = searchParams.get('products');
        if (currentProducts) p.set('products', currentProducts);
        const currentRegions = searchParams.get('regions');
        if (currentRegions) p.set('regions', currentRegions);
        return p.toString() ? `&${p.toString()}` : '';
    }, [searchParams]);

    useEffect(() => {
        if (isOpen) {
            setOpenProducts(new Set((products || []).map((_, i) => i)));
            const allPlanes = {};
            (products || []).forEach((product) => {
                const enabledPlanes = Object.entries(product.planes || {}).filter(
                    ([, pd]) => pd.enabled && pd.product_components?.length > 0
                );
                if (enabledPlanes.length >= 2) {
                    allPlanes[product.product_type] = new Set(enabledPlanes.map((_, i) => i));
                }
            });
            setOpenPlanes(allPlanes);
        } else {
            setOpenProducts(new Set());
            setOpenPlanes({});
        }
    }, [isOpen, products]);

    const toggleProduct = (index) => {
        setOpenProducts(prev => {
            const next = new Set(prev);
            if (next.has(index)) next.delete(index);
            else next.add(index);
            return next;
        });
    };

    const togglePlane = (productType, planeIndex) => {
        setOpenPlanes(prev => {
            const current = prev[productType] || new Set();
            const next    = new Set(current);
        if (next.has(planeIndex)) next.delete(planeIndex);
        else next.add(planeIndex);
            return { ...prev, [productType]: next };
        });
    };

    const navigateToIncidents = (productType, startDate, endDate, componentName = null) => {
        const componentParam = componentName ? `&service=${encodeURIComponent(componentName)}` : '';
        navigate({
            pathname: '/incidents',
            search: `products=${encodeURIComponent(productType)}&range=${encodeURIComponent(formatCustomRange(startDate, endDate))}${regionNavParam}${severityNavParam}${forwardedSearchString}${componentParam}`,
        });
    };

    // Pre-compute product-level segments once (used by both product header and service rows)
    const productSegments = useMemo(() => {
        if (!incidents || !products) return {};
        const map = {};
        products.forEach(product => {
            const productType      = product.product_type;
            const productIncidents = incidents.filter(
                i => normalizeProductType(i.product_type)?.toLowerCase() === productType?.toLowerCase()
            );
            map[productType] = computeUptimeSegments(productIncidents, rangeConfig, inScopeRegionCities);
        });
        return map;
    }, [incidents, products, rangeConfig, inScopeRegionCities]);

    const renderComponentRows = (product, productIndex, planeData, planeIndex, singlePlane, planeType = null) => {
        const productType      = product.product_type;
        const productIncidents = incidents?.filter(
            i => normalizeProductType(i.product_type)?.toLowerCase() === productType?.toLowerCase()
        ) || [];

        return planeData.product_components.map((component, componentIndex) => {
            const componentName     = component.name || component;
            const filtered          = productIncidents.filter(inc => isComponentAffected(inc, componentName, planeType?.toLowerCase() ?? null));
            const segments          = computeUptimeSegments(filtered, rangeConfig, inScopeRegionCities);
            const disruptions       = filtered.filter(i => i.severity?.toLowerCase() === 'disruption');
            const componentForwardedParams = forwardedSearchString
                + `&service=${encodeURIComponent(componentName)}`;

            return (
                <tr
                    key={`comp-${productIndex}-${planeIndex}-${componentIndex}`}
                    className={styles.componentRow}
                >
                    <td className={`${styles.componentNameCell} ${singlePlane ? styles.componentNameSinglePlane : styles.componentNameMultiPlane}`}>
                        {componentName}
                    </td>
                    <td className={styles.barCell}>
                        {isLoading ? (
                            <SkeletonBar segmentCount={rangeConfig.days} />
                        ) : (
                            <BarCellContent
                                segments={segments}
                                disruptions={disruptions}
                                periodStartMs={periodStartMs}
                                periodEndMs={periodEndMs}
                                inScopeRegionCities={inScopeRegionCities}
                                onSegmentClick={(startDate, endDate) => navigateToIncidents(productType, startDate, endDate, componentName)}
                                product={productType}
                                serviceName={componentName}
                                forwardedParams={componentForwardedParams}
                            />
                        )}
                    </td>
                </tr>
            );
        });
    };

    const renderPlaneRow = (product, productIndex, planeType, planeData, planeIndex) => {
        const productType      = product.product_type;
        const isPlaneOpen      = openPlanes[productType]?.has?.(planeIndex) ?? true;
        const productIncidents = incidents?.filter(
            i => normalizeProductType(i.product_type)?.toLowerCase() === productType?.toLowerCase()
        ) || [];
        const planeComponents  = (planeData.product_components || []).map(c => c.name || c);
        const planeIncidents   = productIncidents.filter(inc =>
            planeComponents.some(name => isComponentAffected(inc, name, planeType?.toLowerCase() ?? null))
        );
        const planeSegments    = computeUptimeSegments(planeIncidents, rangeConfig, inScopeRegionCities);
        const serviceCount     = planeData.product_components.length;
        const serviceLabel     = serviceCount === 1 ? '1 service' : `${serviceCount} services`;

        const rows = [];

        rows.push(
            <tr
                key={`plane-${productIndex}-${planeIndex}`}
                className={styles.planeRow}
                onClick={() => togglePlane(productType, planeIndex)}
            >
                <td className={styles.planeNameCell}>
                    <ChevronRight
                        className={`${styles.planeChevron} ${isPlaneOpen ? styles.planeChevronOpen : ''}`}
                    />
                    {planeType} Plane
                    <span className={styles.planeBadge}>
                        ({serviceLabel})
                    </span>
                </td>
                <td className={styles.barCell}>
                    {isLoading ? (
                        <SkeletonBar segmentCount={rangeConfig.days} />
                    ) : (
                        <StatusBarOnly
                            segments={planeSegments}
                            onSegmentClick={(startDate, endDate) => navigateToIncidents(productType, startDate, endDate)}
                            product={productType}
                            serviceName={productType}
                            forwardedParams={forwardedSearchString}
                        />
                    )}
                </td>
            </tr>
        );

        if (isPlaneOpen) {
            rows.push(...renderComponentRows(product, productIndex, planeData, planeIndex, false, planeType));
        }

        return rows;
    };

    const renderProductBlock = (product, productIndex) => {
        const enabledPlanes    = Object.entries(product.planes || {}).filter(
            ([, planeData]) => planeData.enabled && planeData.product_components?.length > 0
        );
        const hasPlanes        = enabledPlanes.length > 0;
        const hasMultiplePlanes = enabledPlanes.length >= 2;
        const isProductOpen    = openProducts.has(productIndex);
        const segments         = productSegments[product.product_type] || [];
        const totalServices    = enabledPlanes.reduce((sum, [, pd]) => sum + (pd.product_components?.length || 0), 0);
        const serviceLabel     = totalServices === 1 ? '1 service' : `${totalServices} services`;

        const rows = [];

        rows.push(
            <tr
                key={`product-${productIndex}`}
                className={styles.productRow}
                onClick={() => hasPlanes && toggleProduct(productIndex)}
            >
                <td
                    className={`${styles.productNameCell} ${styles.productRowSticky}`}
                    style={{ top: productStickyTop }}
                >
                    {hasPlanes && (
                        <ChevronRight
                            className={`${styles.chevron} ${isProductOpen ? styles.chevronOpen : ''}`}
                        />
                    )}
                    {product.product_type}
                    {hasPlanes && (
                        <span className={styles.serviceCount}>
                            ({serviceLabel})
                        </span>
                    )}
                </td>
                <td
                    className={`${styles.barCell} ${styles.productRowSticky}`}
                    style={{ top: productStickyTop }}
                >
                    {isLoading ? (
                        <SkeletonBar segmentCount={rangeConfig.days} />
                    ) : (
                        <StatusBarOnly
                            segments={segments}
                            onSegmentClick={(startDate, endDate) => navigateToIncidents(product.product_type, startDate, endDate)}
                            product={product.product_type}
                            serviceName={product.product_type}
                            forwardedParams={forwardedSearchString}
                        />
                    )}
                </td>
            </tr>
        );

        if (isProductOpen && hasPlanes) {
            if (hasMultiplePlanes) {
                enabledPlanes.forEach(([planeType, planeData], planeIndex) => {
                    rows.push(...renderPlaneRow(product, productIndex, planeType, planeData, planeIndex));
                });
            } else {
                rows.push(...renderComponentRows(product, productIndex, enabledPlanes[0][1], 0, true, enabledPlanes[0][0]));
            }
        }

        return rows;
    };

    return (
        <div className={styles.tableWrapper}>
            <div ref={legendRef} className={styles.legendRow}>
                <span className={styles.legendItem}>
                    <span className={`${styles.legendSwatch} ${styles.legendSwatchOperational}`} />
                    Operational
                </span>
                <span className={styles.legendItem}>
                    <span className={`${styles.legendSwatch} ${styles.legendSwatchDegraded}`} />
                    Partially Operational
                </span>
                <span className={styles.legendItem}>
                    <span className={`${styles.legendSwatch} ${styles.legendSwatchDisruption}`} />
                    Down
                </span>
                <button onClick={() => setIsOpen(prev => !prev)} className={styles.expandToggle}>
                    {isOpen ? 'Collapse All' : 'Expand All'}
                </button>
                <UtcNotice className="ssmutw-ml-auto" />
            </div>

            <table className={styles.table}>
                <thead ref={theadRef}>
                    <tr className={styles.headerRow}>
                        <th className={styles.headerTitleCell} style={{ top: legendBottom }}>
                            <span className={styles.headerTitle}>Service</span>
                        </th>
                        <th className={styles.headerBarCell} style={{ top: legendBottom }}>
                            <div className={styles.headerBarInner}>
                                <div className={styles.headerDates}>
                                    <span>{rangeConfig.label}</span>
                                    <span>{rangeConfig.customEnd ? `${rangeConfig.customEnd.format('MMM D')} (UTC)` : 'Today (UTC)'}</span>
                                </div>
                                <span className={styles.headerUptimeLabel}>
                                    Uptime
                                    <span className={styles.infoIcon}>
                                        <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{ pointerEvents: 'none' }}>
                                            <circle cx="12" cy="12" r="10" />
                                            <line x1="12" y1="11" x2="12" y2="16" />
                                            <line x1="12" y1="7.5" x2="12.01" y2="7.5" />
                                        </svg>
                                        <span className={styles.infoTip}>
                                            <ul className={styles.infoTipList}>
                                                <li>Calculated at the service level only.</li>
                                                <li>Only Disrupted incidents reduce uptime.</li>
                                            </ul>
                                            <span className={styles.infoTipDivider} />
                                            <span className={styles.infoTipFooter}>See <button type="button" className={styles.infoTipHelpBtn} onClick={onHelpClick}>Help</button> for details.</span>
                                        </span>
                                    </span>
                                </span>
                            </div>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {isLoadingProducts
                        ? Array.from({ length: 6 }).map((_, i) => (
                            <SkeletonProductRow
                                key={i}
                                segmentCount={rangeConfig.days}
                                nameWidth={`${30 + (i % 3) * 15}%`}
                            />
                          ))
                        : (products || []).map((product, index) => renderProductBlock(product, index))
                    }
                </tbody>
            </table>
        </div>
    );
};

export default ProductUptimeBar;
